import type { SubmitEvent } from "react";

import { Trans, useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

import { useResetPassword } from "@/api/bale";
import { Button } from "@/components/Button";
import { Card, CardSubtitleHeader } from "@/components/Card";
import { ContentFull, ContentLayout } from "@/components/ContentLayout";
import { ErrorDetail } from "@/components/ErrorDetail";
import { Form } from "@/components/Form";
import { FullScreenLayout } from "@/components/FullScreenLayout";
import { Input } from "@/components/Input";
import { Success } from "@/components/Success";

export function ResetPasswordPage() {
  const params = useParams();
  const resetId = params.resetId!;
  const { t } = useTranslation();
  const resetPassword = useResetPassword();

  function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    const confirmPasswordField = event.currentTarget.elements.namedItem("confirm-password") as HTMLInputElement;

    const form = new FormData(event.currentTarget);
    const password = form.get("password")! as string;
    const confirmPassword = form.get("confirm-password")! as string;
    if (password !== confirmPassword) {
      confirmPasswordField.setCustomValidity(t("passwords-do-not-match"));
    }
    if (!event.currentTarget.reportValidity()) return;
    resetPassword.mutate({ resetId, data: { password } });
  }

  return (
    <FullScreenLayout>
      <ContentLayout title={t("reset-password-title")} wet>
        <ContentFull>
          <div className="mt-26 flex flex-col">
            <Card>
              <CardSubtitleHeader>{t("reset-password-title")}</CardSubtitleHeader>
              {resetPassword.isSuccess && (
                <Success className="mx-4">
                  <Trans
                    i18nKey="reset-password-complete"
                    components={{
                      loginLink: (
                        <Link
                          className="text-[hsl(from_currentcolor_h_s_calc((l+75)/2))] underline hover:text-[hsl(from_currentcolor_h_s_calc((l+50)/2))]"
                          to="/join"
                          state={{ showingForm: "login" }}
                        />
                      ),
                    }}
                  />
                </Success>
              )}
              {!resetPassword.isSuccess && !resetPassword.isError && (
                <div className="flex flex-col gap-2 px-4 font-disinterest">
                  <Trans i18nKey="reset-password-disclaimer" />
                </div>
              )}
              {resetPassword.isError && (
                <div className="rounded-md border border-red-200 bg-red-50 p-4 font-disinterest text-red-700">
                  <ErrorDetail error={resetPassword.error?.info} />
                </div>
              )}
              <Form error={resetPassword.error} onSubmit={handleSubmit} className="flex flex-col gap-4 p-4">
                <Input
                  name="password"
                  type="password"
                  required
                  placeholder={t("password")}
                  disabled={resetPassword.isPending}
                />
                <Input
                  name="confirm-password"
                  type="password"
                  required
                  placeholder={t("confirm-password")}
                  disabled={resetPassword.isPending}
                />
                <div className="flex flex-col gap-2">
                  <Button type="submit" disabled={resetPassword.isPending}>
                    {t("confirm")}
                  </Button>
                </div>
              </Form>
            </Card>
          </div>
        </ContentFull>
      </ContentLayout>
    </FullScreenLayout>
  );
}
